import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { TriangleAlert, HelpCircle } from 'lucide-react';
import s from './ConfirmDialog.module.sass';

// Глобальный хост диалога подтверждения (см. utils/confirmDialog.js).
// Портлируется в корень Nova, если тема активна, чтобы работали её CSS-токены;
// иначе — в body (стили имеют фолбэк-значения для классических тем).
const ConfirmDialog = () => {
    const [opts, setOpts] = useState(null);
    const [closing, setClosing] = useState(false);
    const resolveRef = useRef(null);
    const cancelBtnRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            const { resolve, ...rest } = e.detail || {};
            // Если диалог уже открыт — предыдущему отвечаем «нет»
            if (resolveRef.current) resolveRef.current(false);
            resolveRef.current = resolve || null;
            setClosing(false);
            setOpts(rest);
        };
        window.addEventListener('app:confirm', handler);
        return () => window.removeEventListener('app:confirm', handler);
    }, []);

    const finish = useCallback((answer) => {
        setClosing(true);
        setTimeout(() => {
            if (resolveRef.current) resolveRef.current(answer);
            resolveRef.current = null;
            setOpts(null);
            setClosing(false);
        }, 170);
    }, []);

    useEffect(() => {
        if (!opts) return;
        // Фокус на безопасной кнопке: Enter не должен случайно удалить
        cancelBtnRef.current?.focus();
        const onKey = (e) => {
            if (e.key === 'Escape') finish(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [opts, finish]);

    if (!opts) return null;

    const danger = opts.danger !== false;
    const target = document.querySelector('[data-nova-theme]') || document.body;
    const Icon = danger ? TriangleAlert : HelpCircle;

    return createPortal(
        <div
            className={`${s.overlay} ${closing ? s.closing : ''}`}
            onMouseDown={(e) => { if (e.target === e.currentTarget) finish(false); }}
        >
            <div className={s.card} role="alertdialog" aria-modal="true" aria-label={opts.title || 'Подтверждение'}>
                <div className={`${s.iconWrap} ${danger ? s.iconDanger : s.iconNeutral}`}>
                    <Icon size={20} strokeWidth={2} aria-hidden="true" />
                </div>
                <div className={s.title}>{opts.title || 'Подтвердите действие'}</div>
                {opts.message && <div className={s.message}>{opts.message}</div>}
                <div className={s.actions}>
                    <button
                        type="button"
                        ref={cancelBtnRef}
                        className={s.cancelBtn}
                        title={opts.cancelHint || 'Закрыть окно, ничего не меняя'}
                        onClick={() => finish(false)}
                    >
                        {opts.cancelText || 'Отмена'}
                    </button>
                    <button
                        type="button"
                        className={`${s.confirmBtn} ${danger ? s.dangerBtn : ''}`}
                        title={opts.confirmHint || 'Выполнить действие'}
                        onClick={() => finish(true)}
                    >
                        {opts.confirmText || 'Подтвердить'}
                    </button>
                </div>
            </div>
        </div>,
        target
    );
};

export default ConfirmDialog;
